/** @jsx jsx */
// External libraries
import { jsx } from '@emotion/core';
import { useStoreState } from 'easy-peasy';
import { func, string } from 'prop-types';
import {
  capitalizeFirst,
  Checkbox,
  FieldError,
} from '@checkout/react-components';

// Internal modules
import { features, getContent } from '@/lib';
import { getTermsLinks } from './Components';
import { StyledText } from '@/components/Common';
import { useMountLogging } from '@/lib/reactHooks';
import { checkboxLabelStyleOverrides } from '@/styles';
import { errorStyleOverrides, textStyles } from './styles';
import { logBlurOrFocusEvent } from '../Form/utils';
import { getOnboardTermsKey } from './utils';

const OnboardTerms = ({ changeHandler, type }) => {
  useMountLogging(`${type}OnboardTerms`);
  const checkboxName = `${type}AgreeToTerms`;
  const isSubmitting = useStoreState(({ ui }) => ui.isSubmitting);
  const agreeToTerms = useStoreState(({ form }) => form[checkboxName]);
  const complianceKey = getOnboardTermsKey(type);
  const termsLinks = getTermsLinks(complianceKey, isSubmitting);
  const hasTermsCheckbox = features.isActive(
    `has${capitalizeFirst(type)}TermsAndConditionsCheckbox`,
  );

  if (!hasTermsCheckbox) {
    return (
      <StyledText css={textStyles}>
        {getContent(complianceKey, termsLinks)}
      </StyledText>
    );
  }

  const hasError = Boolean(agreeToTerms?.displayError);
  // Wrapping the <Checkbox /> in a <div> instead of a fragment so that the
  // container <div> will have the margin bottom and not the <Checkbox />, which
  // adds a space between it and the <FieldError/> when it is present.
  return (
    <div>
      <Checkbox
        aria-describedby={hasError ? `${checkboxName}-error` : null}
        aria-invalid={hasError}
        blurHandler={logBlurOrFocusEvent}
        changeHandler={changeHandler}
        checked={agreeToTerms.value}
        disabled={isSubmitting}
        focusHandler={logBlurOrFocusEvent}
        id={checkboxName}
        label={getContent(complianceKey, termsLinks)}
        labelStyleOverrides={checkboxLabelStyleOverrides}
        name={checkboxName}
        required={true}
        {...agreeToTerms}
      />

      {hasError && (
        <FieldError
          id="agreeToTerms-error"
          styleOverrides={errorStyleOverrides}
        >
          {getContent('fieldErrors.signupTermsMissing')}
        </FieldError>
      )}
    </div>
  );
};

OnboardTerms.propTypes = {
  changeHandler: func.isRequired,
  type: string.isRequired,
};

export { OnboardTerms };
